/**
 * Channel capture (§12.1): every share link carries its source through UTM tags and the referrer; the entry
 * records the platform the participant came from. The landing page captures `utm_*`, `ref`, `platform` and
 * `document.referrer` into a session-stored `arrival` record that the entry screen attaches to the entry and
 * the analytics layer attaches as `campaign`. Values are tags only — never free text from the participant —
 * and are truncated to the EventEnvelope limits (key ≤40, value ≤120, referrer ≤200).
 */
import type { SocialPlatform, SourcePlatform } from '@windrop/contracts';

export interface Arrival {
  /** Lottery slug the arrival was captured on (undefined for non-lottery routes). */
  slug?: string;
  sourcePlatform: SourcePlatform;
  /** `utm_*` + `ref` as published by the share link. */
  campaign: Record<string, string>;
  /** Referral code (`?ref=` or `/r/:code`). */
  referralCode?: string;
  /** Referrer origin only (no path, no query), ≤200 chars. */
  referrer?: string;
  landedAt: string;
}

export const ARRIVAL_STORAGE_KEY = 'windrop:arrival';
const CAMPAIGN_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'ref'] as const;

const PLATFORM_HOSTS: Array<[RegExp, SocialPlatform]> = [
  [/(^|\.)(wa\.me|whatsapp\.com|whatsapp\.net)$/i, 'WHATSAPP'],
  [/(^|\.)(instagram\.com|ig\.me|instagr\.am)$/i, 'INSTAGRAM'],
  [/(^|\.)(facebook\.com|fb\.com|fb\.me|m\.me|messenger\.com)$/i, 'FACEBOOK'],
  [/(^|\.)(tiktok\.com)$/i, 'TIKTOK'],
  [/(^|\.)(x\.com|twitter\.com|t\.co)$/i, 'X'],
  [/(^|\.)(t\.me|telegram\.org|telegram\.me)$/i, 'TELEGRAM'],
];

const PLATFORM_NAMES: Record<string, SocialPlatform> = {
  whatsapp: 'WHATSAPP',
  wa: 'WHATSAPP',
  instagram: 'INSTAGRAM',
  ig: 'INSTAGRAM',
  facebook: 'FACEBOOK',
  fb: 'FACEBOOK',
  messenger: 'FACEBOOK',
  tiktok: 'TIKTOK',
  x: 'X',
  twitter: 'X',
  telegram: 'TELEGRAM',
  tg: 'TELEGRAM',
  other: 'OTHER',
};

export function platformFromName(value: string | null | undefined): SocialPlatform | undefined {
  if (!value) return undefined;
  return PLATFORM_NAMES[value.trim().toLowerCase()];
}

export function platformFromReferrer(referrer: string | null | undefined): SocialPlatform | undefined {
  if (!referrer) return undefined;
  let host: string;
  try {
    host = new URL(referrer).hostname;
  } catch {
    return undefined;
  }
  for (const [re, platform] of PLATFORM_HOSTS) if (re.test(host)) return platform;
  return undefined;
}

const clip = (s: string, n: number) => (s.length > n ? s.slice(0, n) : s);

function referrerOrigin(referrer: string | null | undefined): string | undefined {
  if (!referrer) return undefined;
  try {
    const u = new URL(referrer);
    if (typeof window !== 'undefined' && u.origin === window.location.origin) return undefined; // internal nav
    return clip(u.origin, 200);
  } catch {
    return undefined;
  }
}

/** Pure: derive an arrival from a URL search string + referrer. */
export function parseArrival(
  search: string,
  referrer: string | null | undefined,
  slug?: string,
  now = new Date(),
): Arrival {
  const params = new URLSearchParams(search);
  const campaign: Record<string, string> = {};
  for (const key of CAMPAIGN_KEYS) {
    const v = params.get(key);
    if (v && v.trim()) campaign[clip(key, 40)] = clip(v.trim(), 120);
  }
  const explicit = platformFromName(params.get('platform')) ?? platformFromName(campaign.utm_source);
  const sourcePlatform: SourcePlatform = explicit ?? platformFromReferrer(referrer) ?? 'DIRECT';
  const referralCode = campaign.ref;
  const ref = referrerOrigin(referrer);
  const arrival: Arrival = { slug, sourcePlatform, campaign, landedAt: now.toISOString() };
  if (referralCode) arrival.referralCode = referralCode;
  if (ref) arrival.referrer = ref;
  return arrival;
}

function storage(): Storage | null {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : null;
  } catch {
    return null;
  }
}

const key = (slug?: string) => (slug ? `${ARRIVAL_STORAGE_KEY}:${slug}` : ARRIVAL_STORAGE_KEY);

/** Whether the current URL carries attribution that should replace an earlier capture. */
const hasAttribution = (a: Arrival) => Object.keys(a.campaign).length > 0 || a.sourcePlatform !== 'DIRECT';

/**
 * Captures the arrival for `slug` once per session. First touch wins unless the new URL carries explicit
 * attribution (a fresh share link overrides a plain revisit).
 */
export function captureArrival(
  slug?: string,
  loc: { search: string } = window.location,
  referrer = document.referrer,
): Arrival {
  const fresh = parseArrival(loc.search, referrer, slug);
  const store = storage();
  const existing = getArrival(slug);
  if (existing && !hasAttribution(fresh)) return existing;
  try {
    store?.setItem(key(slug), JSON.stringify(fresh));
  } catch {
    /* storage unavailable — attribution is best-effort */
  }
  return fresh;
}

export function getArrival(slug?: string): Arrival | null {
  const store = storage();
  try {
    const raw = store?.getItem(key(slug));
    return raw ? (JSON.parse(raw) as Arrival) : null;
  } catch {
    return null;
  }
}

export function clearArrival(slug?: string): void {
  try {
    storage()?.removeItem(key(slug));
  } catch {
    /* ignore */
  }
}
